import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Snackbar,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Box
} from "@material-ui/core";
import { Edit, Delete, Add, ArrowBack } from "@material-ui/icons";
import { API_POST } from "../../services/api.service";

const SupportDevConfigPage = ({ onBack }) => {
  const [config, setConfig] = useState({
    types: [],
    modules: [],
    categories: [],
    subcategories: []
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
  const [dialog, setDialog] = useState({ open: false, type: '', data: null, mode: 'add' });
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await API_POST("action/getdata", { root: "systemSettingsConfiguration", con: { context: "supportDevConfig" } });
      if (res && res.data) {
        const configData = {
          types: [],
          modules: [],
          categories: [],
          subcategories: []
        };
        
        res.data.forEach(item => {
          if (item.name && item.value) {
            configData[item.name] = item.value;
          }
        });
        
        setConfig(configData);
      }
    } catch (err) {
      console.error("Error loading config:", err);
      setSnackbar({ open: true, message: 'Error loading configuration', color: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Get existing records
      const existingConfig = await API_POST("action/getdata", { root: "systemSettingsConfiguration", con: { context: "supportDevConfig" } });
      
      const configTypes = ['types', 'modules', 'categories', 'subcategories'];
      
      for (const type of configTypes) {
        const configData = {
          name: type,
          value: config[type],
          context: "supportDevConfig"
        };
        
        // Find existing record for this type
        const existingRecord = existingConfig.data && existingConfig.data.find(item => item.name === type);
        
        if (existingRecord) {
          // Update existing record
          await API_POST("action/update", {
            root: "systemSettingsConfiguration",
            querydata: { _id: existingRecord._id },
            body: configData
          });
        } else {
          // Create new record
          await API_POST("action/addRecord", {
            root: "systemSettingsConfiguration",
            body: configData
          });
        }
      }
      
      setSnackbar({ open: true, message: 'Configuration saved successfully', color: 'success' });
    } catch (err) {
      console.error("Error saving config:", err);
      setSnackbar({ open: true, message: 'Error saving configuration', color: 'error' });
    }
  };

  const openDialog = (type, mode = 'add', data = null) => {
    setDialog({ open: true, type, mode, data });
    setFormData(data || {});
  };

  const closeDialog = () => {
    setDialog({ open: false, type: '', data: null, mode: 'add' });
    setFormData({});
  };

  const handleAddItem = () => {
    const { type, mode } = dialog;
    const newItem = formData.name || formData.value || '';
    
    if (!newItem.trim()) {
      setSnackbar({ open: true, message: 'Please enter a value', color: 'error' });
      return;
    }

    if (mode === 'add') {
      setConfig(prev => ({
        ...prev,
        [type]: [...prev[type], newItem]
      }));
    } else if (mode === 'edit') {
      setConfig(prev => ({
        ...prev,
        [type]: prev[type].map((item, index) => 
          index === dialog.data.index ? newItem : item
        )
      }));
    }
    
    closeDialog();
  };

  const handleDeleteItem = (type, index) => {
    setConfig(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const renderConfigSection = (title, type, items) => (
    <Card style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(33, 150, 243, 0.07)' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
          <Typography variant="h6" style={{ fontWeight: 500, color: '#1976d2' }}>
            {title}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => openDialog(type, 'add')}
            size="small"
          >
            Add {title.slice(0, -1)}
          </Button>
        </Box>
        
        <Grid container spacing={1}>
          {items.map((item, index) => (
            <Grid item key={index}>
              <Chip
                label={item}
                onDelete={() => handleDeleteItem(type, index)}
                deleteIcon={<Delete />}
                style={{ margin: 4 }}
                color="primary"
                variant="outlined"
              />
            </Grid>
          ))}
          {items.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                No {title.toLowerCase()} added yet
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', margin: 40 }}>
        <CircularProgress />
        <Typography variant="subtitle1" style={{ marginTop: 16 }}>Loading configuration...</Typography>
      </div>
    );
  }

  return (
    <div style={{ margin: '0 auto', padding: 24 }}>
      <Box display="flex" alignItems="center" marginBottom={3}>
        <IconButton onClick={onBack} style={{ marginRight: 16 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" style={{ fontWeight: 600, color: '#1976d2' }}>
          Support Dev Configuration
        </Typography>
      </Box>

      {renderConfigSection('Types', 'types', config.types)}
      {renderConfigSection('Modules', 'modules', config.modules)}
      {renderConfigSection('Categories', 'categories', config.categories)}


      <Box display="flex" justifyContent="center" marginTop={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          size="large"
          style={{ minWidth: 200, fontWeight: 500 }}
        >
          Save Configuration
        </Button>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === 'add' ? 'Add' : 'Edit'} {dialog.type.slice(0, -1)}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={formData.name || formData.value || ''}
            onChange={(e) => setFormData({ name: e.target.value })}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddItem} color="primary" variant="contained">
            {dialog.mode === 'add' ? 'Add' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        ContentProps={{ 
          style: { 
            backgroundColor: snackbar.color === 'success' ? '#43a047' : '#d32f2f', 
            color: '#fff' 
          } 
        }}
      />
    </div>
  );
};

export default SupportDevConfigPage; 